import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { Modal, Form } from 'antd';
import { applyMask, removeMask } from '@/utils/mask';
import { cepMask } from '@/utils/mask/commonMasks';
import { useDefaultFormErrorNotification } from '@/hooks';
import AddressForm from './AddressForm';
import type { AddressDefaultType, AddressModalProps } from './types';
import { AxiosError } from 'axios';

const AddressModal = ({
  visible,
  onClose,
  onOk,
  loading,
  address,
  simpleForm,
  fieldsDisabled,
  removeDelivery = true,
  removeDescription = true,
  removeMain = true,
  ...props
}: AddressModalProps) => {
  const intl = useIntl();
  const [form] = Form.useForm<AddressDefaultType>();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();

  useEffect(() => {
    if (visible && !isEmpty(address)) {
      form.setFieldsValue({
        ...address,
        zipCode: applyMask(address?.zipCode, cepMask),
      });
    }
  }, [visible, address]);

  const handleOk = async () => {
    try {
      const formValues = await form.validateFields();

      const obj = {
        ...formValues,
        zipCode: removeMask(formValues.zipCode),
      };

      if (address) {
        await onOk({ ...obj, id: address.id });
      } else {
        await onOk(obj);
      }

      handleCancel();
    } catch (err) {
      const error = err as AxiosError;

      if (!error.isAxiosError) {
        sendFormErrorNotification(err);
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();

    onClose();
  };

  return (
    <Modal
      title={intl.formatMessage({ id: 'commons.address' })}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={765}
      confirmLoading={loading}
      okButtonProps={{ disabled: fieldsDisabled }}
      destroyOnClose
      {...props}
    >
      <AddressForm
        form={form}
        address={address}
        simpleForm={simpleForm}
        isRequired
        hasAddress={props.hasAddress}
        fieldsDisabled={fieldsDisabled}
        removeDescription={removeDescription}
        removeDelivery={removeDelivery}
        removeMain={removeMain}
      />
    </Modal>
  );
};

export default AddressModal;
